import FormQuill from "@babackapp/react-hook-forms-components/form-quill-input";
import FormSaveButton from "@babackapp/react-hook-forms-components/form-save-button";
import { Form, FormField } from "@babackapp/ui/components/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFieldArray, useForm, useFormContext } from "react-hook-form";
import { z } from "zod";

export const customErrorKeys = [
  "countryNotAvailable",
  "notCompliant",
  "notCompliantPOS",
  "orderExpired",
  "needValidationText",
  "notFullFilled",
] as const;

export const customMessagesKeys = [
  "headerMessage",
  "legalDoc",
  "articleSelectionMessage",
  "articleSelectionSubtitle",
  "receiptHeaderInfo",
  "formAddress",
  "confirmationMessage",
  "endProcess",
  "endProcessWithoutPhysicalReturn",
  "reasonStepInfo",
  "resolutionHeaderInfo",
] as const;

export const customKeys = [...customErrorKeys, ...customMessagesKeys] as const;

export type CustomKey = (typeof customKeys)[number];

const CustomMessagesSettingsSchema = z.object({
  values: z.array(
    z.object({
      lang: z.string(),
      text: z.string(),
    }),
  ),
});

type CustomMessagesSettingsFormValues = z.infer<
  typeof CustomMessagesSettingsSchema
>;

type CustomMessagesSettingsFormProps = {
  customKey: CustomKey;
  onSubmit: (data: CustomMessagesSettingsFormValues["values"]) => void;
  defaultValues?: {
    values: CustomMessagesSettingsFormValues["values"];
  };
};

function CustomMessagesSettingsFormFields() {
  const { control } = useFormContext<CustomMessagesSettingsFormValues>();

  const valuesFields = useFieldArray({
    control: control,
    name: "values",
  });

  return valuesFields.fields.map((_, index) => (
    <FormField
      key={index}
      control={control}
      name={`values.${index}`}
      render={({ field }) => (
        <FormQuill
          label={field.value.lang}
          value={field.value.text}
          onChange={(value) =>
            field.onChange({
              lang: field.value.lang,
              text: value,
            })
          }
        />
      )}
    />
  ));
}

export function CustomMessagesSettingsForm({
  defaultValues,
  onSubmit,
}: CustomMessagesSettingsFormProps) {
  const form = useForm<CustomMessagesSettingsFormValues>({
    resolver: zodResolver(CustomMessagesSettingsSchema),
    defaultValues,
  });

  const _onSubmit = (data: CustomMessagesSettingsFormValues) => {
    return onSubmit(data.values);
  };

  return (
    <Form {...form}>
      <form
        className="flex flex-col justify-start gap-8 mt-8"
        onSubmit={form.handleSubmit(_onSubmit)}
      >
        <CustomMessagesSettingsFormFields />

        <FormSaveButton />
      </form>
    </Form>
  );
}
